import { gql, type TypedDocumentNode } from '@apollo/client'
import { QueryRef, useApolloClient, useReadQuery } from '@apollo/client/react'
import { createFileRoute, Outlet, useLocation } from '@tanstack/react-router'
import ProfileHeader from '@web/src/features/profile/profile-header/profile-header'
import type { ProfileLayoutHeadData } from '@web/src/features/profile/profile-layout-head'
import ProfileMenu from '@web/src/features/profile/profile-menu/profile-menu'
import ProfileTaglineCard from '@web/src/features/profile/profile-tagline-card/profile-tagline-card'
import { queryAndPreload } from '@web/src/lib/apollo/query-and-preload'
import { ProfileInfo, ProfileMediaInfo } from '@web/src/lib/fragments'
import type { ProfileLayoutQuery, ProfileLayoutQueryVariables } from 'types/graphql'

export const PROFILE_LAYOUT_QUERY: TypedDocumentNode<ProfileLayoutQuery, ProfileLayoutQueryVariables> = gql`
  query ProfileLayoutQuery($profileId: String!) {
    isBlocked(profileId: $profileId)
    isFriend(profileId: $profileId) {
      id
      friendId
      approved
    }
    isFollowing(profileId: $profileId) {
      id
      followingId
    }
    friendsCounts(filter: { profileId: $profileId }) {
      friends
      pending
      requests
    }
    profile: profile(id: $profileId) {
      ...ProfileInfo
    }
  }
  ${ProfileInfo}
  ${ProfileMediaInfo}
`

export const Route = createFileRoute('/(auth)/(main-layout)/profile/$profileId')({
  component: Layout,
  loader: async ({ context, params }): Promise<ProfileLayoutHeadData & { queryRef: unknown }> => {
    const { queryData, queryRef } = await queryAndPreload(context, PROFILE_LAYOUT_QUERY, {
      variables: { profileId: params.profileId },
    })
    return { queryRef, profileName: queryData?.profile?.name }
  },
})

function Layout() {
  const apolloClient = useApolloClient()
  const { queryRef } = Route.useLoaderData()
  const { data } = useReadQuery(queryRef as QueryRef<ProfileLayoutQuery>)
  const location = useLocation()
  const pathname = location.pathname

  const isAboutActive = pathname.includes('/about')

  const handleRefetch = async () => {
    await apolloClient.refetchQueries({ include: [PROFILE_LAYOUT_QUERY] })
  }

  return (
    <div className="mx-auto w-full max-w-[90rem] px-0">
      <ProfileHeader
        isBlocked={data.isBlocked}
        isFollowing={data.isFollowing}
        isFriend={data.isFriend}
        onQueryRefetch={handleRefetch}
        profile={data.profile}
      />
      <div className="mx-auto mb-4 grid max-w-[90rem] grid-cols-2 gap-3 px-2 md:grid-cols-4">
        <div className="col-span-1 mt-0 hidden w-[80%] justify-self-center md:col-span-1 md:mt-6 md:block">
          <ProfileMenu isBlocked={data.isBlocked} onQueryRefetch={handleRefetch} profile={data.profile} />
        </div>
        <div className="col-span-2 mt-10 md:col-span-2 md:mt-0">
          <div className="flex w-full flex-col gap-2">
            <ProfileTaglineCard hideTagline={!isAboutActive} profile={data.profile} />
            <Outlet />
          </div>
        </div>
      </div>
    </div>
  )
}
